import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NotificationLevel } from '@shared/services/notification/notification-level.model';
import { SnackbarService } from '@shared/services/notification/snackbar.service';

const ACTION_MESSAGES: Record<string, { success?: string; error?: string; cancelled?: string }> = {
  UPDATE_PASSWORD: {
    success: 'USER_PROFILE.PASSWORD_UPDATED',
    error: 'USER_PROFILE.PASSWORD_UPDATE_FAILED',
  },
  CONFIGURE_TOTP: {
    success: 'USER_PROFILE.AUTH_APP_CONFIGURED',
    error: 'USER_PROFILE.AUTH_APP_CONFIGURE_FAILED',
  },
};

export type KeycloakActionStatus = 'success' | 'error' | 'cancelled';

@Injectable({ providedIn: 'root' })
export class KeycloakActionNotifierService {
  private readonly snackbar = inject(SnackbarService);
  private readonly translate = inject(TranslateService);

  private pending: Array<{ action: string; status: KeycloakActionStatus }> = [];
  private ready = false;

  flushPending(): void {
    this.ready = true;
    const queued = this.pending;
    this.pending = [];
    queued.forEach(({ action, status }) => this.show(action, status));
  }

  handle(action: string, status: KeycloakActionStatus): void {
    if (!ACTION_MESSAGES[action]) {
      return;
    }
    if (!this.ready) {
      this.pending.push({ action, status });
      return;
    }
    this.show(action, status);
  }

  private show(action: string, status: KeycloakActionStatus): void {
    const keys = ACTION_MESSAGES[action];
    if (!keys) {
      return;
    }
    switch (status) {
      case 'success':
        if (keys.success) {
          this.snackbar.openSnackbar(this.translate.instant(keys.success));
        }
        break;
      case 'error':
        if (keys.error) {
          this.snackbar.openSnackbar(this.translate.instant(keys.error), NotificationLevel.ERROR);
        }
        break;
      case 'cancelled':
        if (keys.cancelled) {
          this.snackbar.openSnackbar(
            this.translate.instant(keys.cancelled),
            NotificationLevel.INFO
          );
        }
        break;
    }
  }
}
